import * as React from 'react';
import { Panel } from '../../../components/containers/Panel';
import {
  GetMe_me_organizations_workspaces_settings,
  GetQueueMetadata_workspace_queueMeta,
  GetSocialProfiles_workspace_socialProfiles
} from '../../../models/legacy-graphql';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { ButtonGroup } from 'components/containers/ButtonGroup';
import { Button } from 'components/actions/Button';
import { ButtonColorsEnum } from 'constants/button-colors';
import { Tabs } from 'components/actions/Tabs';
import { Tab } from 'components/actions/Tabs/Tab';
import {
  SwitchButtonType,
  OnUpdateWorkspacePauseSettings,
  OnUpdatePausePerProfileCategory,
  PauseStates
} from './Pause.controller';
import { ColoredCategory } from './ColoredCategory';
import { SocialProfile } from 'components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../Profiles/Profiles.helpers';
import { CustomPublishingRow } from './CustomPublishingRow';
import { ProfilePauseModal } from './ProfilePauseModal';
import { CategoryPauseModal } from './CategoryPauseModal';
import classes from './Pause.module.scss';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { BoxNotification } from 'components/containers/BoxNotification';
import { FormElementAction } from '../../../models/basic-objects';
import { GetCategoriesQuery } from '../../../models/graphql';
import { useContext } from 'react';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type PauseViewProps = {
  selectedWorkspaceSettings: GetMe_me_organizations_workspaces_settings | null;
  switches: SwitchButtonType[];
  onUpdateWorkspacePauseSettings: OnUpdateWorkspacePauseSettings;
  onUpdatePerProfileCategory: OnUpdatePausePerProfileCategory;
  categories: GetCategoriesQuery['workspace']['categories'];
  socialProfiles: GetSocialProfiles_workspace_socialProfiles[];
  queueMeta: GetQueueMetadata_workspace_queueMeta[];
  profileModal: GetSocialProfiles_workspace_socialProfiles | null;
  setProfileModal: (value: GetSocialProfiles_workspace_socialProfiles | null) => void;
  categoryModal: GetCategoriesQuery['workspace']['categories'][number] | null;
  switchButtonSubmitted: boolean;
  customPublishingSubmitted: boolean;
  loading: boolean;
  loadingError: boolean;
  setCategoryModal: (value: GetCategoriesQuery['workspace']['categories'][number] | null) => void;
};
export const PauseView = (props: PauseViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { isTemporallySuspended } = useSubscriptionStatus()
  const renderButtonSwitch = ({ title, description, item }: SwitchButtonType): JSX.Element => {
    const paused = isTemporallySuspended ? true : props.selectedWorkspaceSettings?.[item];
    return (
      <div className={'grid-x align-justify margin-top-small-24'} key={title}>
        <div
          className={`cell small-12 medium-auto ${classes.ButtonSwitchText} margin-right-small-12 margin-right-large-24`}>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <div className={classNames('cell small-12 medium-shrink margin-small-left-12 ', classes.ButtonGroupPause)}>
          <ButtonGroup size={'medium'}>
            <Button
              disabled={isTemporallySuspended ? true : props.switchButtonSubmitted}
              type={'button'}
              variant={!paused ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={props.onUpdateWorkspacePauseSettings({ item, active: true })}>
              {PauseStates.Active}
            </Button>
            <Button
              disabled={isTemporallySuspended ? true : props.switchButtonSubmitted}
              type={'button'}
              variant={paused ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={props.onUpdateWorkspacePauseSettings({ item, active: false })}>
              {PauseStates.Paused}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  const socialProfiles = props.socialProfiles.map((profile: GetSocialProfiles_workspace_socialProfiles, i: number) => {
    const action: FormElementAction = (value, complete) => {
      props.onUpdatePerProfileCategory(
        props.categories.map(category => ({
          categoryId: category.id,
          paused: value === PauseStates.Paused,
          socialProfileId: profile.id
        })),
        complete
      );
    };
    return (
      <CustomPublishingRow
        id={profile.id}
        key={profile.id}
        noBorder={props.socialProfiles.length - 1 === i}
        submitted={props.customPublishingSubmitted}
        profile={profile}
        action={action}
        onCustomize={() => {
          props.setProfileModal(profile);
        }}
        queueMeta={props.queueMeta}>
        <SocialProfile
          size={'medium'}
          socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
          image={profile.photoUrl}
          label
        />
      </CustomPublishingRow>
    );
  });

  const categories = props.categories.map((category, i) => {
    const action: FormElementAction = (value, complete) => {
      props.onUpdatePerProfileCategory(
        props.socialProfiles.map(profile => ({
          categoryId: category.id,
          paused: value === PauseStates.Paused,
          socialProfileId: profile.id
        })),
        complete
      );
    };
    return (
      <CustomPublishingRow
        id={category.id}
        key={category.id}
        noBorder={props.categories.length - 1 === i}
        submitted={props.customPublishingSubmitted}
        category={category}
        action={action}
        onCustomize={() => {
          props.setCategoryModal(category);
        }}
        queueMeta={props.queueMeta}>
        <ColoredCategory category={category} />
      </CustomPublishingRow>
    );
  });
  const renderTabContent = (content: JSX.Element[]): JSX.Element[] | JSX.Element => {
    if (props.selectedWorkspaceSettings?.queuesPaused) {
      return (
        <span className={classes.PausedMessage}>
          Your whole Queue is currently paused. To pause specific Categories or Profiles, unpause your whole Queue
          first.
        </span>
      );
    }
    return content;
  };
  if (!props.socialProfiles.length && !props.loading) {
    return (
      <CenteredContentLayout headerTitle={'Pause Settings'} hasSidebar>
        <Panel>
          <span className={classNames(classes.PausedMessage, 'margin-bottom-small-16')}>
            <p>Pause Settings will be available once you have a Social Profile in your Workspace. <a onClick={() => beacon('article', '698', { type: 'modal' })}>Learn more</a></p>
            <p><Link to={'/settings/profiles'}>Connect a Profile</Link></p>
          </span>
        </Panel>
      </CenteredContentLayout>
    );
  }
  const renderContent = () => {
    if (props.loadingError) {
      return (
        <div className={'grid-x align-center'}>
          <div className={'cell small-12'}>
            <div className={classes.BoxMessageError}>
              <BoxNotification type={'error'}>
                Something went wrong. Please refresh the page and try again.
              </BoxNotification>
            </div>
          </div>
        </div>
      );
    }
    return (
      <Panel>
        {props.switches.map(renderButtonSwitch)}
        <PanelHeader
          title={'Custom Pause Controls'}
          tooltip={
            <div>
              <p className={classes.TooltipFontSize}>
                If you want to continue publishing most content as normal, but need to pause specific content, you can
                control which Profiles or Categories are paused.
              </p>
              <ul>
                <li>
                  <p className={'no-b-m small-text'}>
                    Pause a whole Category - this will pause all Posts in the Categories you set to &apos;Paused&apos;
                  </p>
                </li>
                <li>
                  <p className={'no-b-m small-text'}>
                    Pause a whole Profile - this will pause all Posts in the Profiles you set to &apos;Paused&apos;
                  </p>
                </li>
                <li>
                  <p className={'no-b-m small-text'}>
                    Customize - you can choose a Category, and then only pause specific Profiles within that Category;
                    or vice versa - choose a Profile, and then pause specific Categories within that Profile.
                  </p>
                </li>
              </ul>
              <p className={'no-b-m small-text'}>
                Any Profile or Category that has custom pause settings applied will appear as &quot;Mixed&quot;.
              </p>
              <p className={'no-b-m small-text'}>
                <a onClick={() => beacon('article', '698', { type: 'modal' })}>Learn more</a>
              </p>
            </div>
          }
        />
        <Tabs>
          <Tab
            header={'Pause per Category'}
            contentClassName={classes.CustomTab}
            headerClassName={classes.CustomHeader}>
            {renderTabContent(categories)}
          </Tab>
          <Tab header={'Pause per Profile'} contentClassName={classes.CustomTab} headerClassName={classes.CustomHeader}>
            {renderTabContent(socialProfiles)}
          </Tab>
        </Tabs>
      </Panel>
    );
  };
  return (
    <>
      <CenteredContentLayout headerTitle={'Pause Settings'} loading={props.loading} hasSidebar>
        {renderContent()}
      </CenteredContentLayout>
      {props.profileModal ? (
        <ProfilePauseModal
          submitted={props.customPublishingSubmitted}
          onClose={() => props.setProfileModal(null)}
          onUpdatePerProfileCategory={props.onUpdatePerProfileCategory}
          categories={props.categories}
          queueMeta={props.queueMeta}
          profile={props.profileModal}
        />
      ) : null}
      {props.categoryModal ? (
        <CategoryPauseModal
          submitted={props.customPublishingSubmitted}
          onClose={() => props.setCategoryModal(null)}
          onUpdatePerProfileCategory={props.onUpdatePerProfileCategory}
          profiles={props.socialProfiles}
          queueMeta={props.queueMeta}
          category={props.categoryModal}
        />
      ) : null}
    </>
  );
};
